import { useEffect, useRef, type RefObject } from 'react';

/**
 * Lightweight scroll-reveal hook using IntersectionObserver.
 *
 * SSG-safe: elements start visible (no flash), then get the hidden class
 * client-side before IntersectionObserver reveals them on scroll.
 * Respects prefers-reduced-motion — skips all animation.
 */
export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // If any part of the element is already within the viewport on load, leave
    // it visible. Hiding it here would drop its opacity to 0 and — because the
    // observer's 0.15 threshold never fires for a section only peeking in below
    // the hero/wave — the sliver would stay blank (showing the page background
    // through as black) until the user scrolls. So only hide content that's
    // fully below the fold; that content still animates in on scroll as before.
    const rect = el.getBoundingClientRect();
    const alreadyInView = rect.top < window.innerHeight && rect.bottom > 0;
    if (alreadyInView) return;

    el.classList.add('reveal--hidden');

    // Small delay to let the browser paint the hidden state before observing
    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.remove('reveal--hidden');
            el.classList.add('reveal--visible');
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.15,
          rootMargin: options?.rootMargin ?? '0px 0px -40px 0px',
        },
      );
      observer.observe(el);
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.rootMargin]);

  return ref;
}

/**
 * Observes all children matching `[data-reveal]` within a container.
 * Each child is revealed with a stagger delay based on its DOM index.
 */
export function useStaggerReveal<T extends HTMLElement = HTMLElement>(
  options?: { threshold?: number; staggerMs?: number; rootMargin?: string },
): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const children = container.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!children.length) return;

    const stagger = options?.staggerMs ?? 100;

    // Only hide children that are fully below the fold on load. A child already
    // peeking into the viewport stays visible so it never flashes blank (the
    // 0.1 threshold wouldn't fire for a thin sliver) — matching useScrollReveal.
    const toReveal = Array.from(children).filter((child) => {
      const rect = child.getBoundingClientRect();
      return !(rect.top < window.innerHeight && rect.bottom > 0);
    });
    if (!toReveal.length) return;

    toReveal.forEach((child) => child.classList.add('reveal--hidden'));

    const raf = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target as HTMLElement;
            const index = toReveal.indexOf(el);
            setTimeout(() => {
              el.classList.remove('reveal--hidden');
              el.classList.add('reveal--visible');
            }, index * stagger);
            observer.unobserve(el);
          }
        },
        {
          threshold: options?.threshold ?? 0.1,
          rootMargin: options?.rootMargin ?? '0px 0px -30px 0px',
        },
      );

      toReveal.forEach((child) => observer.observe(child));
    });

    return () => cancelAnimationFrame(raf);
  }, [options?.threshold, options?.staggerMs, options?.rootMargin]);

  return ref;
}
